<template>
  <div class="admin-page">
    <aside class="admin-sidebar">
      <div class="admin-brand">
        <div class="brand-mark">+</div>
        <div>
          <strong>Novalab</strong>
          <span>{{ $t('adminNews.contentAdmin') }}</span>
        </div>
      </div>
      <nav>
        <a class="active"><i class="el-icon-document"></i> {{ $t('adminNews.newsNav') }}</a>
        <router-link to="/"><i class="el-icon-monitor"></i> {{ $t('adminNews.backHome') }}</router-link>
      </nav>
    </aside>

    <main class="admin-main">
      <header class="admin-header">
        <div>
          <div class="eyebrow">{{ $t('adminNews.eyebrow') }}</div>
          <h1>{{ $t('adminNews.title') }}</h1>
          <p>{{ $t('adminNews.desc') }}</p>
        </div>
        <div class="header-actions">
          <div class="language-switch" :aria-label="$t('adminNews.languageSwitch')">
            <button
              v-for="locale in locales"
              :key="locale.value"
              type="button"
              :class="{ active: $i18n.locale === locale.value }"
              @click="$setLocale(locale.value)">
              {{ locale.label }}
            </button>
          </div>
          <el-button icon="el-icon-refresh-left" @click="resetDefault">{{ $t('adminNews.resetDefault') }}</el-button>
          <el-button type="primary" icon="el-icon-plus" @click="openEditor()">{{ $t('adminNews.createNews') }}</el-button>
        </div>
      </header>

      <section class="admin-card">
        <el-table :data="sortedNews" stripe>
          <el-table-column prop="date" :label="$t('adminNews.date')" width="120" />
          <el-table-column :label="$t('adminNews.newsTitle')" min-width="260">
            <template slot-scope="{ row }">
              <div class="title-cell">
                <strong>{{ row.title }}</strong>
                <span>{{ row.excerpt }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="category" :label="$t('adminNews.category')" width="120" />
          <el-table-column prop="readTime" :label="$t('adminNews.readTime')" width="120" />
          <el-table-column :label="$t('adminNews.featured')" width="100" align="center">
            <template slot-scope="{ row }">
              <el-switch :value="row.featured" @change="setFeatured(row.id)" />
            </template>
          </el-table-column>
          <el-table-column :label="$t('adminNews.actions')" width="160" align="right">
            <template slot-scope="{ row }">
              <el-button type="text" icon="el-icon-edit" @click="openEditor(row)">{{ $t('adminNews.edit') }}</el-button>
              <el-button type="text" class="danger-link" icon="el-icon-delete" @click="removeNews(row)">{{ $t('adminNews.delete') }}</el-button>
            </template>
          </el-table-column>
        </el-table>
      </section>
    </main>

    <el-dialog :title="editingId ? $t('adminNews.editNews') : $t('adminNews.addNews')" :visible.sync="dialogVisible" width="760px">
      <el-form ref="form" :model="form" :rules="rules" label-width="92px">
        <el-tabs v-model="activeEditorLocale" class="locale-tabs">
          <el-tab-pane :label="$t('adminNews.zhTab')" name="zh">
            <el-form-item :label="$t('adminNews.newsTitle')" prop="locales.zh.title">
              <el-input v-model="form.locales.zh.title" maxlength="60" show-word-limit />
            </el-form-item>
            <el-form-item :label="$t('adminNews.excerpt')" prop="locales.zh.excerpt">
              <el-input v-model="form.locales.zh.excerpt" type="textarea" :rows="3" maxlength="120" show-word-limit />
            </el-form-item>
            <div class="form-grid">
              <el-form-item :label="$t('adminNews.category')" prop="locales.zh.category">
                <el-input v-model="form.locales.zh.category" :placeholder="$t('adminNews.categoryPlaceholder')" />
              </el-form-item>
              <el-form-item :label="$t('adminNews.readTime')" prop="locales.zh.readTime">
                <el-input v-model="form.locales.zh.readTime" :placeholder="$t('adminNews.readTimePlaceholder')" />
              </el-form-item>
            </div>
          </el-tab-pane>
          <el-tab-pane :label="$t('adminNews.enTab')" name="en">
            <el-form-item :label="$t('adminNews.newsTitle')" prop="locales.en.title">
              <el-input v-model="form.locales.en.title" maxlength="90" show-word-limit />
            </el-form-item>
            <el-form-item :label="$t('adminNews.excerpt')" prop="locales.en.excerpt">
              <el-input v-model="form.locales.en.excerpt" type="textarea" :rows="3" maxlength="180" show-word-limit />
            </el-form-item>
            <div class="form-grid">
              <el-form-item :label="$t('adminNews.category')" prop="locales.en.category">
                <el-input v-model="form.locales.en.category" :placeholder="$t('adminNews.enCategoryPlaceholder')" />
              </el-form-item>
              <el-form-item :label="$t('adminNews.readTime')" prop="locales.en.readTime">
                <el-input v-model="form.locales.en.readTime" :placeholder="$t('adminNews.enReadTimePlaceholder')" />
              </el-form-item>
            </div>
          </el-tab-pane>
        </el-tabs>

        <div class="form-grid">
          <el-form-item :label="$t('adminNews.publishDate')" prop="date">
            <el-date-picker v-model="form.date" type="date" value-format="yyyy-MM-dd" :placeholder="$t('adminNews.datePlaceholder')" />
          </el-form-item>
          <el-form-item :label="$t('adminNews.icon')" prop="icon">
            <el-select v-model="form.icon" :placeholder="$t('adminNews.iconPlaceholder')">
              <el-option v-for="icon in icons" :key="icon" :label="icon" :value="icon" />
            </el-select>
          </el-form-item>
        </div>
        <el-form-item :label="$t('adminNews.background')" prop="bg">
          <el-select v-model="form.bg" :placeholder="$t('adminNews.backgroundPlaceholder')">
            <el-option v-for="bg in backgrounds" :key="bg.value" :label="bg.label" :value="bg.value" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('adminNews.featured')">
          <el-switch v-model="form.featured" :active-text="$t('adminNews.setFeatured')" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">{{ $t('adminNews.cancel') }}</el-button>
        <el-button type="primary" @click="saveNews">{{ $t('adminNews.save') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { defaultNews, getNewsItems, localizeNewsItems, normalizeNewsItem, resetNewsItems, saveNewsItems } from '@/data/newsStore'

const emptyForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  bg: 'linear-gradient(135deg,#0a1628,#0d2952)',
  icon: 'el-icon-document',
  featured: false,
  locales: {
    zh: {
      title: '',
      excerpt: '',
      readTime: '3 分钟',
      category: '公司动态'
    },
    en: {
      title: '',
      excerpt: '',
      readTime: '3 min',
      category: 'Company News'
    }
  }
})

export default {
  name: 'NewsAdmin',
  data() {
    return {
      newsItems: [],
      dialogVisible: false,
      editingId: '',
      activeEditorLocale: 'zh',
      form: emptyForm(),
      icons: ['el-icon-cpu', 'el-icon-s-cooperation', 'el-icon-finished', 'el-icon-trophy', 'el-icon-document', 'el-icon-data-analysis']
    }
  },
  computed: {
    locales() {
      return this.$tm('nav.locales')
    },
    rules() {
      return {
        'locales.zh.title': [{ required: true, message: this.$t('adminNews.validation.zhTitle'), trigger: 'blur' }],
        'locales.zh.excerpt': [{ required: true, message: this.$t('adminNews.validation.zhExcerpt'), trigger: 'blur' }],
        'locales.zh.category': [{ required: true, message: this.$t('adminNews.validation.zhCategory'), trigger: 'blur' }],
        'locales.zh.readTime': [{ required: true, message: this.$t('adminNews.validation.zhReadTime'), trigger: 'blur' }],
        'locales.en.title': [{ required: true, message: this.$t('adminNews.validation.enTitle'), trigger: 'blur' }],
        'locales.en.excerpt': [{ required: true, message: this.$t('adminNews.validation.enExcerpt'), trigger: 'blur' }],
        'locales.en.category': [{ required: true, message: this.$t('adminNews.validation.enCategory'), trigger: 'blur' }],
        'locales.en.readTime': [{ required: true, message: this.$t('adminNews.validation.enReadTime'), trigger: 'blur' }],
        date: [{ required: true, message: this.$t('adminNews.validation.date'), trigger: 'change' }],
        icon: [{ required: true, message: this.$t('adminNews.validation.icon'), trigger: 'change' }],
        bg: [{ required: true, message: this.$t('adminNews.validation.background'), trigger: 'change' }]
      }
    },
    backgrounds() {
      return [
        { label: this.$t('adminNews.backgrounds.deepBlue'), value: 'linear-gradient(135deg,#0a1628,#0d2952)' },
        { label: this.$t('adminNews.backgrounds.aiPurple'), value: 'linear-gradient(135deg,#1a0533 0%,#2d1b69 50%,#0a1628 100%)' },
        { label: this.$t('adminNews.backgrounds.qcGreen'), value: 'linear-gradient(135deg,#003d2e,#006d52)' },
        { label: this.$t('adminNews.backgrounds.honorGold'), value: 'linear-gradient(135deg,#3d2000,#7a4500)' },
        { label: this.$t('adminNews.backgrounds.insightPurple'), value: 'linear-gradient(135deg,#1a003d,#3d0080)' }
      ]
    },
    localizedNews() {
      return localizeNewsItems(this.newsItems, this.$i18n.locale)
    },
    sortedNews() {
      return [...this.localizedNews].sort((a, b) => new Date(b.date) - new Date(a.date))
    }
  },
  mounted() {
    this.newsItems = getNewsItems()
  },
  methods: {
    persist(items = this.newsItems) {
      const normalized = items.map(normalizeNewsItem)
      this.newsItems = normalized
      saveNewsItems(normalized)
    },
    openEditor(row) {
      this.editingId = row ? row.id : ''
      this.form = row ? normalizeNewsItem(row) : emptyForm()
      this.activeEditorLocale = this.$i18n.locale
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    saveNews() {
      this.$refs.form.validate(valid => {
        if (!valid) return

        const item = normalizeNewsItem({
          ...this.form,
          id: this.editingId || `news-${Date.now()}`
        })
        let next = this.editingId
          ? this.newsItems.map(news => news.id === this.editingId ? item : news)
          : [item, ...this.newsItems]

        if (item.featured) {
          next = next.map(news => ({ ...news, featured: news.id === item.id }))
        }

        this.persist(next)
        this.dialogVisible = false
        this.$message.success(this.$t('adminNews.saved'))
      })
    },
    setFeatured(id) {
      this.persist(this.newsItems.map(item => ({ ...item, featured: item.id === id })))
      this.$message.success(this.$t('adminNews.featuredUpdated'))
    },
    removeNews(row) {
      this.$confirm(this.$t('adminNews.confirmDeleteMessage', { title: row.title }), this.$t('adminNews.deleteTitle'), { type: 'warning' })
        .then(() => {
          const next = this.newsItems.filter(item => item.id !== row.id)
          this.persist(next)
          this.$message.success(this.$t('adminNews.deleted'))
        })
        .catch(() => {})
    },
    resetDefault() {
      this.$confirm(this.$t('adminNews.resetMessage'), this.$t('adminNews.resetTitle'), { type: 'warning' })
        .then(() => {
          resetNewsItems()
          this.newsItems = defaultNews.map(normalizeNewsItem)
          this.$message.success(this.$t('adminNews.resetDone'))
        })
        .catch(() => {})
    }
  }
}
</script>

<style lang="less" scoped>
.admin-page {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  background: #f5f7fb;
}

.admin-sidebar {
  background: #071016;
  color: #fff;
  padding: 28px 20px;
}

.admin-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 34px;
}

.brand-mark {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0a5c8a, #00c9a7);
  font-size: 24px;
  font-weight: 800;
}

.admin-brand strong,
.admin-brand span {
  display: block;
}

.admin-brand span {
  margin-top: 3px;
  font-size: 12px;
  color: rgba(255,255,255,.46);
}

.admin-sidebar nav {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.admin-sidebar a {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 42px;
  padding: 0 12px;
  border-radius: 8px;
  color: rgba(255,255,255,.72);
  cursor: pointer;
}

.admin-sidebar a.active,
.admin-sidebar a:hover {
  background: rgba(0,201,167,.12);
  color: #00c9a7;
}

.admin-main {
  padding: 36px 42px;
}

.admin-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}

.eyebrow {
  color: #00a88b;
  font-size: 12px;
  letter-spacing: 3px;
  font-weight: 800;
  margin-bottom: 8px;
}

.admin-header h1 {
  font-size: 30px;
  margin-bottom: 8px;
}

.admin-header p {
  color: #718096;
  font-size: 14px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.language-switch {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border: 1px solid rgba(10,92,138,.18);
  border-radius: 18px;
  background: rgba(10,92,138,.06);
}

.language-switch button {
  height: 28px;
  padding: 0 10px;
  border: none;
  border-radius: 15px;
  background: transparent;
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all .2s;
}

.language-switch button.active {
  background: linear-gradient(135deg,#0a5c8a,#00c9a7);
  color: #fff;
}

.admin-card {
  background: #fff;
  border-radius: 12px;
  padding: 18px;
  border: 1px solid #e8edf3;
  box-shadow: 0 14px 40px rgba(15, 35, 55, .06);
}

.title-cell strong {
  display: block;
  color: #1a1a2e;
  margin-bottom: 6px;
}

.title-cell span {
  display: block;
  color: #718096;
  font-size: 12px;
  line-height: 1.5;
}

.danger-link {
  color: #f56c6c !important;
}

.locale-tabs {
  margin-bottom: 8px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.form-grid /deep/ .el-date-editor,
.form-grid /deep/ .el-select,
/deep/ .el-select {
  width: 100%;
}

@media (max-width: 900px) {
  .admin-page {
    grid-template-columns: 1fr;
  }

  .admin-sidebar {
    position: static;
  }

  .admin-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    justify-content: flex-start;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
